import { addCalendarDays, todayBusinessDate, type IsoDate } from "@wtc/business-date";

import { DEAL_WON_STAGE } from "../deal-stages";
import { type QualityDeal, type QualityIssue, type Severity } from "./types";
import { qualityRules } from "./rules-definitions";

/**
 * Evaluate every data-quality rule over a set of deals.
 *
 * `now` is injectable and defaults to the current time; it is immediately reduced
 * to the BUSINESS calendar day, which is what the date rules actually compare
 * against. Two defects this shape closes:
 *
 *  - the rules used to compare against a `new Date()` captured at MODULE LOAD, so
 *    a long-running server judged "overdue" against the day it booted;
 *  - and a server-derived day is the wrong day. Vercel functions run in UTC, so
 *    for the last five or six hours of every Central day a UTC "today" names
 *    tomorrow and every deal closing today reads as overdue.
 */
export function evaluateQualityRules(
  deals: QualityDeal[],
  now: Date = new Date()
): QualityIssue[] {
  const issues: QualityIssue[] = [];
  const today: IsoDate = todayBusinessDate(now);

  for (const rule of qualityRules) {
    const affectedDeals = deals.filter((deal) => rule.check(deal, today));

    if (affectedDeals.length > 0) {
      const totalRevenue = affectedDeals.reduce((sum, deal) => sum + deal.revenue, 0);

      issues.push({
        id: rule.id,
        name: rule.name,
        description: rule.description,
        severity: rule.severity,
        count: affectedDeals.length,
        totalAffectedRevenue: totalRevenue,
        suggestedAction: rule.suggestedAction,
        deals: affectedDeals.map((deal) => rule.extractData(deal)),
      });
    }
  }

  return issues;
}

/** Rule metadata (name/description/suggestedAction) by issue id. */
export function getQualityRuleMeta(
  issueId: string
): { id: string; name: string; description: string; suggestedAction: string } | null {
  const rule = qualityRules.find((r) => r.id === issueId);
  if (!rule) return null;
  return {
    id: rule.id,
    name: rule.name,
    description: rule.description,
    suggestedAction: rule.suggestedAction,
  };
}

/**
 * The deterministic field edit that resolves an issue.
 *
 * The dates it stamps are BUSINESS calendar days. `now.toISOString().split("T")[0]`
 * — what this used — is the UTC day, which names TOMORROW for the last five or six
 * hours of every Central day, so "set the close date to today" set it to tomorrow
 * every evening. `won_at` stays a full instant, because it records a moment.
 */
export function getSuggestedFix(
  issueId: string,
  now: Date = new Date()
): Partial<QualityDeal> | null {
  const today = todayBusinessDate(now);
  switch (issueId) {
    case "revenue_without_won":
      return { stage: DEAL_WON_STAGE, won_at: now.toISOString() };
    case "stage_mismatch_contract_no_revenue":
      return { stage: "proposal" };
    case "missing_forecast_data":
      return { close_date: addCalendarDays(today, 30), close_probability: 50 };
    case "overdue_deals":
      return { close_date: today };
    default:
      return null;
  }
}

/**
 * Plain-English description of the EXACT field change `getSuggestedFix` will
 * apply, so the user sees what "Apply Suggested Fix" actually does before
 * clicking. Returns null for issues that have no deterministic auto-fix (use
 * "Ask AI to fix" for those judgment calls).
 */
export function describeSuggestedFix(issueId: string, deal: QualityDeal): string | null {
  switch (issueId) {
    case "revenue_without_won":
      return `Mark this deal Won (stage → Won) and set the won date to today — it has $${deal.revenue?.toLocaleString() ?? 0} of revenue but isn't marked won.`;
    case "stage_mismatch_contract_no_revenue":
      return "Move this deal back to Proposal — it's at Contract Signed with $0 revenue.";
    case "missing_forecast_data":
      return "Set a close date 30 days out and a 50% close probability (rough defaults — use Ask AI to fix for a per-deal estimate).";
    case "overdue_deals":
      return "Set the close date to today (its close date is in the past).";
    default:
      return null;
  }
}

/**
 * Build the instruction sent to Meridian for an "Ask AI to fix" launch. Meridian
 * reads it, proposes the field edits via the `update_deal` tool, and the user
 * approves the exact diff in the standard HITL approval card — no deterministic
 * guess is applied. Best for the judgment-call issues (unassigned, at-risk,
 * health-red, negative-margin) that have no safe auto-fix.
 */
export function buildDealFixAiPrompt(
  issue: { id: string; name: string; description: string; suggestedAction: string },
  deal: QualityDeal
): string {
  const facts = [
    `id: ${deal.id}`,
    `title: ${deal.title}`,
    deal.company_name ? `company: ${deal.company_name}` : null,
    `stage: ${deal.stage}`,
    `revenue: ${deal.revenue ?? 0}`,
    deal.close_probability != null ? `close_probability: ${deal.close_probability}%` : "close_probability: (none)",
    deal.close_date ? `close_date: ${deal.close_date}` : "close_date: (none)",
    deal.cost != null ? `cost: ${deal.cost}` : null,
  ]
    .filter(Boolean)
    .join(", ");

  return [
    `A deal in our CRM has a data-quality issue and I'd like you to fix it.`,
    ``,
    `Issue: ${issue.name} — ${issue.description}`,
    `Recommended action: ${issue.suggestedAction}`,
    ``,
    `Deal (${facts}).`,
    ``,
    `Please look up this deal by its id, decide the correct field edits to resolve the issue, and use update_deal (or mark_deal_won / mark_deal_lost if appropriate). Show me the proposed changes for approval before applying them.`,
  ].join("\n");
}

/**
 * Impact weight per issue id — how much one affected deal costs the 0–100 score.
 * Unlisted ids fall back to 5.
 */
const QUALITY_IMPACT_WEIGHTS: Record<string, number> = {
  revenue_without_won: 15,
  negative_margin: 15,
  stage_mismatch_contract_no_revenue: 10,
  overdue_deals: 10,
  high_value_at_risk: 10,
  health_status_red: 8,
  missing_forecast_data: 5,
  unassigned_deals: 5,
};

export function calculateWeightedQualityScore(issues: QualityIssue[]): number {
  let score = 100;
  issues.forEach((issue) => {
    const weight = QUALITY_IMPACT_WEIGHTS[issue.id] || 5;
    score -= (issue.count * weight) / 100;
  });

  return Math.max(0, Math.round(score));
}

export type { Severity };
